import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { cn } from "../lib/utils";
import { AtSign, Eye, Check, MessageSquare, Plus, CheckCircle2, Inbox as InboxIcon, Compass } from "lucide-react";
import { Avatar } from "../components/board/BoardCells";
import { Spinner } from "../components/ui";
import { timeAgo, personOf, renderWithMentions } from "../components/board/social";

const KIND_ICON = { created: Plus, comment: MessageSquare, column: CheckCircle2 };

export default function Inbox() {
  const { t, lang, entities, refreshInbox, inboxUnread } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mentions");
  const [mentions, setMentions] = useState(null);
  const [watching, setWatching] = useState(null);

  const loadMentions = useCallback(async () => {
    const { data } = await api.get("/notifications?limit=50");
    setMentions(data);
  }, []);
  const loadWatching = useCallback(async () => {
    const { data } = await api.get("/inbox/watching?limit=50");
    setWatching(data);
    try { await api.post("/inbox/watching/seen"); } catch (err) { console.debug("mark watching seen failed:", err?.message || err); }
    refreshInbox();
  }, [refreshInbox]);

  useEffect(() => { loadMentions(); }, [loadMentions]);
  useEffect(() => { if (tab === "watching" && watching === null) loadWatching(); }, [tab, watching, loadWatching]);

  const goto = (n) => {
    if (n.link) { navigate(n.link); return; }
    const key = entities.find((e) => e.id === n.entity_id)?.key;
    if (key && n.space_id && n.project_id) navigate(`/entity/${key}/space/${n.space_id}/project/${n.project_id}?item=${n.item_id}`);
  };
  const openMention = async (n) => {
    if (!n.read) { try { await api.post("/notifications/read", { ids: [n.id] }); } catch (err) { console.debug("mark notification read failed:", err?.message || err); } }
    goto(n); loadMentions(); refreshInbox();
  };
  const markAllMentions = async () => { try { await api.post("/notifications/read-all"); } catch (err) { console.debug("mark all read failed:", err?.message || err); } loadMentions(); refreshInbox(); };
  const dismissMention = async (e, n) => {
    e.stopPropagation();
    try { await api.post("/notifications/dismiss", { ids: [n.id] }); } catch (err) { console.debug("dismiss notification failed:", err?.message || err); }
    loadMentions(); refreshInbox();
  };

  const TABS = [
    { k: "mentions", label: t("inbox_mentions"), Icon: AtSign, badge: inboxUnread?.unread_mentions },
    { k: "watching", label: t("inbox_watching"), Icon: Eye, badge: inboxUnread?.unread_watching },
  ];

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="inbox-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><InboxIcon className="w-5 h-5" /></span>
        <div>
          <h1 className="font-heading text-2xl font-medium text-text-primary">{t("nav_inbox")}</h1>
          <p className="text-sm text-text-secondary">{t("inbox_subtitle")}</p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b mb-5" data-testid="inbox-tabs">
        {TABS.map(({ k, label, Icon, badge }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`inbox-tab-${k}`}
            className={cn("inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === k ? "border-primary text-text-primary" : "border-transparent text-text-secondary hover:text-text-primary")}>
            <Icon className="w-4 h-4" />{label}
            {badge > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold inline-flex items-center justify-center">{badge}</span>}
          </button>
        ))}
      </div>

      {tab === "mentions" && (
        mentions === null ? <Loader />
        : mentions.items.length === 0 ? <Empty icon={AtSign} text={t("inbox_no_mentions")} />
        : (
          <div className="rounded-2xl border bg-surface overflow-hidden">
            {mentions.unread_count > 0 && (
              <div className="flex justify-end px-4 py-2 border-b">
                <button onClick={markAllMentions} data-testid="inbox-mark-all" className="inline-flex items-center gap-1 text-[11px] font-medium text-text-secondary hover:text-text-primary transition-colors">
                  <Check className="w-3.5 h-3.5" />{t("mentions_mark_all")}
                </button>
              </div>
            )}
            <div className="divide-y">
              {mentions.items.map((n) => (
                <div key={n.id} data-testid={`inbox-mention-${n.id}`}
                  className={cn("group w-full flex items-start gap-3 px-4 py-3.5 hover:bg-surface-hover transition-colors", !n.read && "bg-primary/[0.04]")}>
                  <button onClick={() => openMention(n)} data-testid={`inbox-mention-open-${n.id}`}
                    className="flex-1 min-w-0 flex items-start gap-3 text-left">
                    {!n.read ? <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" /> : <span className="w-1.5 shrink-0" />}
                    {n.kind && n.kind.startsWith("advisory") ? (
                      <>
                        <span className="w-[30px] h-[30px] rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0"><Compass className="w-4 h-4" /></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-text-primary"><span className="font-semibold">{n.actor_name || "Advisory"}</span> · <span className="font-medium">{n.title}</span></p>
                          <p className="text-[13px] text-text-secondary truncate">{n.text}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <Avatar person={personOf(n)} size={30} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-text-primary"><span className="font-semibold">{n.actor_name}</span> {t("mentions_on")} <span className="font-medium">{n.item_name}</span></p>
                          <p className="text-[13px] text-text-secondary truncate">{renderWithMentions(n.text, [])}</p>
                        </div>
                      </>
                    )}
                  </button>
                  <span className="text-[11px] text-text-secondary shrink-0 mt-0.5">{timeAgo(n.created_at, lang)}</span>
                  <button onClick={(e) => dismissMention(e, n)} title={t("mentions_dismiss")} aria-label={t("mentions_dismiss")}
                    data-testid={`inbox-mention-dismiss-${n.id}`}
                    className="shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-text-secondary hover:text-white hover:bg-primary transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100">
                    <Check className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {tab === "watching" && (
        watching === null ? <Loader />
        : watching.items.length === 0 ? <Empty icon={Eye} text={t("inbox_no_watching")} />
        : (
          <div className="rounded-2xl border bg-surface overflow-hidden divide-y">
            {watching.items.map((a) => {
              const Icon = KIND_ICON[a.kind] || MessageSquare;
              const label = a.kind === "comment" ? t("inbox_posted") : a.kind === "created" ? t("inbox_added") : t("inbox_status_changed");
              return (
                <button key={a.id} onClick={() => goto(a)} data-testid={`inbox-watch-${a.id}`}
                  className={cn("w-full flex items-start gap-3 px-4 py-3.5 text-left hover:bg-surface-hover transition-colors", a.unread && "bg-primary/[0.04]")}>
                  <span className="relative shrink-0 mt-0.5"><Avatar person={personOf(a)} size={30} />
                    <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-background border flex items-center justify-center text-text-secondary"><Icon className="w-2.5 h-2.5" /></span>
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary"><span className="font-semibold">{a.actor_name}</span> {label} <span className="font-medium">{a.item_name}</span></p>
                    <p className="text-[12px] text-text-secondary truncate">
                      {a.board_name}{a.text ? ` · ${a.text}` : ""}
                    </p>
                  </div>
                  <span className="text-[11px] text-text-secondary shrink-0 mt-0.5">{timeAgo(a.created_at, lang)}</span>
                </button>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

const Loader = () => <div className="py-16 flex justify-center"><Spinner className="w-7 h-7 text-text-secondary" /></div>;
const Empty = ({ icon: Icon, text }) => (
  <div className="py-16 text-center text-sm text-text-secondary" data-testid="inbox-empty">
    <Icon className="w-8 h-8 mx-auto mb-3 opacity-40" />{text}
  </div>
);
